import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ShieldAlert } from 'lucide-react';
import { useAuth } from '../services/AuthContext';

interface RequestAccessProps {
  email: string;
}

const RequestAccess: React.FC<RequestAccessProps> = ({ email }) => {
  const { logout } = useAuth();

  const nextSteps = [
    'Ask an organization admin or master user to add this Google account to the correct MLST organization.',
    'Confirm that the role they assign includes the workspace access you expect.',
    'Return and sign in again with the same Google account once the invite is in place.',
  ];

  return (
    <div className="brand-shell brand-grid">
      <div className="relative z-10 flex min-h-screen items-center px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto grid w-full max-w-5xl gap-6 lg:grid-cols-[minmax(0,1.05fr)_420px]">
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="brand-card overflow-hidden bg-brand-navy px-8 py-10 text-brand-cloud sm:px-10 sm:py-12"
          >
            <div className="flex h-14 w-14 items-center justify-center rounded-[1.35rem] bg-white/10 shadow-brand">
              <img
                src="/logos/combination_mark_yellow.svg"
                alt="Machine Learning Street Talk"
                className="h-8 w-auto"
              />
            </div>

            <p className="mt-10 brand-kicker border-white/15 bg-white/10 text-brand-cloud">Organization Access</p>
            <h1 className="mt-6 max-w-2xl font-headlines text-5xl font-bold tracking-tight text-brand-cloud sm:text-6xl">
              That Google account is not on an approved MLST workspace.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-brand-cloud/74">
              You signed in as <span className="font-headlines text-brand-yellow">{email}</span>. Access is now
              managed directly by organization admins inside the application settings.
            </p>

            <div className="mt-10 rounded-[1.75rem] border border-white/10 bg-white/[0.06] p-6">
              <h2 className="font-headlines text-xl font-bold text-brand-cloud">What to do next</h2>
              <div className="mt-6 space-y-4">
                {nextSteps.map((step, index) => (
                  <div key={step} className="flex items-start gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-yellow font-headlines text-sm font-bold text-brand-navy">
                      0{index + 1}
                    </div>
                    <p className="text-sm leading-7 text-brand-cloud/74">{step}</p>
                  </div>
                ))}
              </div>
            </div>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.08 }}
            className="brand-card p-8 text-center sm:p-10"
          >
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-brand-yellow text-brand-navy">
              <ShieldAlert className="h-8 w-8" />
            </div>
            <p className="mt-8 brand-kicker">Access Denied</p>
            <h2 className="mt-6 font-headlines text-3xl font-bold tracking-tight text-brand-navy">
              Admin approval required
            </h2>
            <p className="mt-4 text-base leading-8 text-brand-navy/68">
              Once an admin adds <span className="font-headlines text-brand-navy">{email}</span> to the right
              organization and role, sign in again to continue.
            </p>

            <div className="mt-8 brand-divider" />

            <div className="mt-8 flex flex-col gap-3">
              <button onClick={() => void logout()} className="brand-button-primary w-full">
                Logout
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </motion.section>
        </div>
      </div>
    </div>
  );
};

export default RequestAccess;
